import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, BadgePercent, Box, CheckCircle2, ImagePlus, Images, Inbox, LogOut, Pencil, Plus, RefreshCw, Sparkles, Trash2, X } from "lucide-react";
import { Logo } from "./Home";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["CNC INSERTS", "VMC TOOLING", "END MILLS", "DRILLS"];
const EMPTY_FORM = { name: "", category: "CNC INSERTS", grade: "", coating: "", dims: "", app: "", price: "", stock: "Available", image: "", offer: "", sizes: "", show_image: true };
const QUOTE_STATUSES = ["new", "contacted", "closed"];

const imgSrc = (u) => !u ? "" : (u.startsWith("/api") ? `${BACKEND}${u}` : u);

function fmtErr(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).join(" ");
  if (typeof detail === "string") return detail;
  return fallback;
}

function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginErr, setLoginErr] = useState("");
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [library, setLibrary] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formErr, setFormErr] = useState("");
  const [toast, setToast] = useState("");
  const [photo, setPhoto] = useState(null);
  const [images, setImages] = useState([]); // list of urls for this product
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(""); // "studio" | "action" | ""
  const [showLibrary, setShowLibrary] = useState(false);
  const [batch, setBatch] = useState(null); // {status,total,done,failed}

  const authed = { withCredentials: true };

  useEffect(() => {
    axios.get(`${API}/auth/me`, authed)
      .then(r => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const load = () => {
    axios.get(`${API}/products`).then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get(`${API}/quotes`, authed).then(r => setQuotes(r.data.quotes || [])).catch(() => {});
    axios.get(`${API}/files`, authed).then(r => setLibrary(r.data.files || [])).catch(() => {});
  };

  useEffect(() => { if (user) load(); }, [user]);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    try {
      const r = await axios.post(`${API}/auth/login`, loginForm, authed);
      setUser(r.data);
    } catch (err) {
      setLoginErr(fmtErr(err, "Login failed."));
    }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, authed).catch(() => {});
    setUser(false);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, category: p.category, grade: p.grade || "", coating: p.coating || "", dims: p.dims || "", app: p.app || "", price: p.price || "", stock: p.stock || "Available", image: p.image || "", offer: p.offer || "", sizes: (p.sizes || []).join(", "), show_image: p.show_image !== false });
    const imgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : []));
    setImages(imgs);
    setPhoto(null);
    setFormErr("");
    setShowLibrary(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => { setEditingId(null); setForm(EMPTY_FORM); setPhoto(null); setImages([]); setFormErr(""); };

  const pickPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setPhoto(f);
  };

  const addLibraryImage = (url) => {
    setImages(im => im.includes(url) ? im : [...im, url]);
    setShowLibrary(false);
  };

  const removeImage = (url) => setImages(im => im.filter(x => x !== url));

  const generateImage = async (style) => {
    if (!form.name.trim()) { setFormErr("Enter a product name first."); return; }
    setGenerating(style);
    setFormErr("");
    try {
      const r = await axios.post(`${API}/generate-image`, { product_name: form.name, category: form.category, style }, { ...authed, timeout: 90000 });
      setImages(im => [...im, r.data.url]);
      axios.get(`${API}/files`, authed).then(rr => setLibrary(rr.data.files || [])).catch(() => {});
      notify(style === "action" ? "Action photo generated." : "Studio photo generated.");
    } catch (err) {
      setFormErr(fmtErr(err, "Image generation failed."));
    }
    setGenerating("");
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setFormErr("");
    let imgs = [...images];
    if (photo) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", photo);
        const up = await axios.post(`${API}/upload`, fd, { ...authed, headers: { "Content-Type": "multipart/form-data" } });
        imgs = [...imgs, up.data.url];
      } catch (err) {
        setUploading(false);
        setFormErr(fmtErr(err, "Photo upload failed."));
        return;
      }
      setUploading(false);
      setPhoto(null);
    }
    const payload = { ...form, image: imgs[0] || form.image, images: imgs, sizes: form.sizes.split(",").map(s => s.trim()).filter(Boolean) };
    try {
      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, payload, authed);
        notify("Product updated.");
      } else {
        await axios.post(`${API}/products`, payload, authed);
        notify("Product added.");
      }
      resetForm();
      load();
    } catch (err) {
      setFormErr(fmtErr(err));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await axios.delete(`${API}/products/${id}`, authed).catch(() => {});
    notify("Product deleted.");
    load();
  };

  const setQuoteStatus = async (reference, status) => {
    try {
      const r = await axios.put(`${API}/quotes/${reference}/status`, { status }, authed);
      setQuotes(qs => qs.map(q => q.reference === reference ? r.data : q));
      notify(`Marked as ${status}.`);
    } catch (err) {
      notify(fmtErr(err, "Could not update status."));
    }
  };

  const pollBatch = async () => {
    try {
      const r = await axios.get(`${API}/products/generate-status`, authed);
      setBatch(r.data);
      if (r.data.status === "running") setTimeout(pollBatch, 4000);
      else if (r.data.status === "done") { load(); notify(`Photos done — ${r.data.done - r.data.failed} generated, ${r.data.failed} failed.`); }
    } catch { /* ignore */ }
  };

  const generateAll = async () => {
    try {
      const r = await axios.post(`${API}/products/generate-all`, {}, authed);
      setBatch(r.data);
      notify("Generating photos for all products…");
      setTimeout(pollBatch, 4000);
    } catch (err) {
      notify(fmtErr(err, "Could not start generation."));
    }
  };

  if (checking) return <div className="admin-shell"><div className="admin-loading" data-testid="admin-loading">Loading…</div></div>;

  if (!user) {
    return (
      <div className="admin-shell">
        <form className="admin-login" onSubmit={doLogin} data-testid="admin-login-form">
          <Logo />
          <p className="eyebrow">KX / ADMIN</p>
          <h1>Sign in</h1>
          {loginErr && <p className="form-error" data-testid="admin-login-error">{loginErr}</p>}
          <input type="email" placeholder="Email" required value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} data-testid="admin-email-input"/>
          <input type="password" placeholder="Password" required value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} data-testid="admin-password-input"/>
          <button className="button button-primary" type="submit" data-testid="admin-login-button">Sign in</button>
          <a href="/" className="text-link" data-testid="admin-back-link"><ArrowLeft size={14}/> Back to site</a>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <aside className="admin-side">
        <Logo compact />
        <nav>
          <button className={tab === "products" ? "active" : ""} onClick={() => setTab("products")} data-testid="tab-products"><Box size={15}/> Products</button>
          <button className={tab === "quotes" ? "active" : ""} onClick={() => setTab("quotes")} data-testid="tab-quotes"><Inbox size={15}/> Quotes</button>
        </nav>
        <button className="admin-logout" onClick={doLogout} data-testid="admin-logout-button"><LogOut size={14}/> Sign out</button>
      </aside>

      <main className="admin-main">
        {toast && <div className="admin-toast" data-testid="admin-toast">{toast}</div>}

        {tab === "products" && (
          <>
            <div className="batch-bar" data-testid="batch-bar">
              <button className="photo-tool-btn" onClick={generateAll} disabled={batch && batch.status === "running"} data-testid="generate-all-button"><Sparkles size={15}/> {batch && batch.status === "running" ? "Generating…" : "Generate all product photos"}</button>
              {batch && batch.status === "running" && (
                <>
                  <div className="progress"><i style={{ width: `${batch.total ? (batch.done / batch.total) * 100 : 0}%` }}/></div>
                  <span data-testid="batch-progress">{batch.done} / {batch.total}{batch.failed ? ` · ${batch.failed} failed` : ""}</span>
                </>
              )}
              {batch && batch.status === "done" && <span data-testid="batch-done">Done — {batch.done - batch.failed} generated{batch.failed ? `, ${batch.failed} failed` : ""}</span>}
            </div>

            <form className="admin-form" onSubmit={submitProduct} data-testid="product-form">
              <div className="admin-form-head">
                <h2>{editingId ? "Edit product" : "Add new product"}</h2>
                {editingId && <button type="button" className="text-link" onClick={resetForm} data-testid="cancel-edit-button"><X size={14}/> Cancel edit</button>}
              </div>
              {formErr && <p className="form-error" data-testid="product-form-error">{formErr}</p>}
              <div className="admin-form-grid">
                <input placeholder="Product name *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="product-name-input"/>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="product-category-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input placeholder="Grade (e.g. KX-P25)" value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} data-testid="product-grade-input"/>
                <input placeholder="Coating (e.g. TiAlN)" value={form.coating} onChange={e => setForm(f => ({ ...f, coating: e.target.value }))} data-testid="product-coating-input"/>
                <input placeholder="Dimensions" value={form.dims} onChange={e => setForm(f => ({ ...f, dims: e.target.value }))} data-testid="product-dims-input"/>
                <input placeholder="Application" value={form.app} onChange={e => setForm(f => ({ ...f, app: e.target.value }))} data-testid="product-app-input"/>
                <input placeholder="Price (e.g. ₹180 — ₹420 / pc)" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} data-testid="product-price-input"/>
                <input placeholder="Stock (e.g. Available / 500 pcs)" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} data-testid="product-stock-input"/>
                <input placeholder="Offer (e.g. 10% off this week)" value={form.offer} onChange={e => setForm(f => ({ ...f, offer: e.target.value }))} data-testid="product-offer-input"/>
                <input placeholder="Sizes (comma separated)" value={form.sizes} onChange={e => setForm(f => ({ ...f, sizes: e.target.value }))} data-testid="product-sizes-input"/>
              </div>

              <div className="photo-tools" data-testid="photo-tools">
                <label className="photo-tool-btn" data-testid="product-photo-label">
                  <input type="file" accept="image/*" capture="environment" onChange={pickPhoto} data-testid="product-photo-input" style={{display:"none"}}/>
                  <ImagePlus size={15}/> {photo ? photo.name : "Upload photo"}
                </label>
                <button type="button" className="photo-tool-btn" onClick={() => setShowLibrary(s => !s)} data-testid="photo-library-button"><Images size={15}/> Photo library ({library.length})</button>
                <button type="button" className="photo-tool-btn" onClick={() => generateImage("studio")} disabled={!!generating} data-testid="generate-studio-button"><Sparkles size={15}/> {generating === "studio" ? "Generating…" : "AI studio photo"}</button>
                <button type="button" className="photo-tool-btn" onClick={() => generateImage("action")} disabled={!!generating} data-testid="generate-action-button"><Sparkles size={15}/> {generating === "action" ? "Generating…" : "AI action photo"}</button>
              </div>

              {showLibrary && (
                <div className="photo-library" data-testid="photo-library">
                  {library.length === 0 && <p className="admin-empty">No photos yet. Upload or generate one.</p>}
                  <div className="library-grid">
                    {library.map(f => (
                      <button type="button" key={f.id} className="library-item" onClick={() => addLibraryImage(f.url)} data-testid={`library-${f.id}`}>
                        <img src={imgSrc(f.url)} alt={f.original_filename || "library photo"}/>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {images.length > 0 && (
                <>
                  <label className="show-image-toggle" data-testid="show-image-toggle">
                    <input type="checkbox" checked={form.show_image} onChange={e => setForm(f => ({ ...f, show_image: e.target.checked }))} data-testid="show-image-checkbox"/>
                    <span>Show main photo on catalogue card <small>(off = photos appear only on the product page)</small></span>
                  </label>
                  <div className="selected-images" data-testid="selected-images">
                    {images.map((u, i) => (
                      <div className="selected-image" key={u}>
                        <img src={imgSrc(u)} alt={`Product ${i + 1}`}/>
                        {i === 0 && <span className="main-tag">MAIN</span>}
                        <button type="button" onClick={() => removeImage(u)} aria-label="Remove image" data-testid={`remove-image-${i}`}><X size={12}/></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <button className="button button-primary" type="submit" disabled={uploading || !!generating} data-testid="product-submit-button"><Plus size={15}/> {uploading ? "Uploading photo…" : editingId ? "Save changes" : "Add product"}</button>
            </form>

            <div className="admin-list" data-testid="product-list">
              {products.map(p => (
                <div className="admin-row" key={p.id} data-testid={`product-row-${p.id}`}>
                  <div className="admin-row-thumb">
                    {(p.images && p.images[0]) || p.image ? <img src={imgSrc((p.images && p.images[0]) || p.image)} alt={p.name}/> : <div className="thumb-empty"/>}
                  </div>
                  <div className="admin-row-main">
                    <b>{p.name}</b>
                    <span>{p.category} · {p.grade || "—"} · {p.price || "—"} · Stock: {p.stock || "Available"}</span>
                    {p.offer && <span className="row-offer"><BadgePercent size={11}/> {p.offer}</span>}
                  </div>
                  <div className="admin-row-actions">
                    <button onClick={() => startEdit(p)} aria-label={`Edit ${p.name}`} data-testid={`edit-${p.id}-button`}><Pencil size={14}/></button>
                    <button onClick={() => deleteProduct(p.id)} aria-label={`Delete ${p.name}`} data-testid={`delete-${p.id}-button`}><Trash2 size={14}/></button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="admin-empty" data-testid="products-empty">No products yet. Add your first one above.</p>}
            </div>
          </>
        )}

        {tab === "quotes" && (
          <div className="admin-list" data-testid="quote-list">
            <div className="admin-form-head">
              <h2>Quote requests</h2>
              <button className="text-link" onClick={load} data-testid="refresh-quotes-button"><RefreshCw size={14}/> Refresh</button>
            </div>
            {quotes.map(q => (
              <div className="admin-row quote-row" key={q.reference} data-testid={`quote-row-${q.reference}`}>
                <div className="admin-row-main">
                  <b>{q.reference} — {q.name} <span className={`status-pill status-${q.status || "new"}`} data-testid={`status-${q.reference}`}>{(q.status || "new").toUpperCase()}</span></b>
                  <span>{q.company || "—"} · {q.email} · {q.phone}</span>
                  <span>{(q.products || []).join(", ") || "No products selected"}</span>
                  {q.message && <span className="row-message">{q.message}</span>}
                </div>
                <div className="quote-status-actions">
                  {QUOTE_STATUSES.map(s => (
                    <button key={s} className={`status-btn ${(q.status || "new") === s ? "active" : ""} status-btn-${s}`} onClick={() => setQuoteStatus(q.reference, s)} data-testid={`quote-${q.reference}-${s}`}>
                      {s === "closed" ? <CheckCircle2 size={12}/> : null}{s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {quotes.length === 0 && <p className="admin-empty" data-testid="quotes-empty">No quote requests yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}

export default Admin;
